import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, DropdownMenu, DropdownMenuItem, Spinner } from '../components/common';
import { Reader, ReaderChapter, ReaderHandle, ReaderOverlay } from '../components/reader/Reader';
import SceneToolsSheet from '../components/book/SceneToolsSheet';
import ScenePhraseSheet from '../components/book/ScenePhraseSheet';
import BookCompanion from '../components/book/BookCompanion';
import { ReaderSelection } from '../lib/reader/selection';
import { renderInlineMarkdown } from '../lib/reader/inlineMarkdown';
import {
  useBookReader,
  useGenerateBookPreview,
  useGenerateScenePreview,
  useGenerateScenePlan,
  useSaveBookReadingPosition,
  useSetBookAutoGenerate,
  useSyncBook,
  useUpdateSceneBody,
  type BookReaderScene,
  type BookReaderChapter,
} from '../hooks/useBookReader';

// A scene contributes prose once its body is non-empty.
const hasProse = (s: BookReaderScene) => !!(s.body && s.body.trim());

// A scene has a plan (scenos planas) once it has at least one plan point.
const hasPlan = (s: BookReaderScene) => s.planPoints.length > 0;

// Split a scene body into paragraph blocks (blank-line separated). Each becomes
// its own reader block so word offsets line up with the rendered <p> exactly.
const splitParagraphs = (text: string): string[] =>
  text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

// Reader block ids embed the scene id: "<sceneId>__<paraIdx>".
const sceneIdOfBlock = (blockId: string) => blockId.split('__')[0];

// First scene (in book order) matching a predicate, tagged with its chapter
// index so the autogenerate loop only ever runs "one chapter ahead".
const findFrontier = (
  chapters: BookReaderChapter[],
  predicate: (s: BookReaderScene) => boolean
) => {
  for (let ci = 0; ci < chapters.length; ci += 1) {
    const scene = chapters[ci].scenes.find(predicate);
    if (scene) return { sceneId: scene.id, chapterIndex: ci };
  }
  return null;
};

export default function BookReaderPage() {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: book, isLoading, refetch } = useBookReader(bookId);
  const setAutoGen = useSetBookAutoGenerate(bookId);
  const genScene = useGenerateScenePreview(bookId);
  const genPlan = useGenerateScenePlan(bookId);
  const genBook = useGenerateBookPreview(bookId);
  const savePosition = useSaveBookReadingPosition();
  const updateBody = useUpdateSceneBody(bookId);
  const syncBook = useSyncBook(bookId);

  const readerRef = useRef<ReaderHandle>(null);
  const [chapterIndex, setChapterIndex] = useState(0);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [sheet, setSheet] = useState<{ sceneId: string; seedText: string | null } | null>(null);
  // A reworded phrase the reader selected — opens the wording sheet.
  const [phraseSel, setPhraseSel] = useState<ReaderSelection | null>(null);
  const [companionOpen, setCompanionOpen] = useState(false);
  // Scenes currently being preview-generated (manual or autogenerate).
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());

  const chapters = useMemo(() => book?.chapters ?? [], [book]);
  const autoGenerate = !!book?.autoGenerate;

  // Generate the next missing artifact for a scene: its plan first, then prose.
  const requestGenerate = (sceneId: string, step: 'plan' | 'prose') => {
    setGeneratingIds((prev) => new Set(prev).add(sceneId));
    const mutation = step === 'plan' ? genPlan : genScene;
    mutation.mutate(sceneId, {
      onSettled: () =>
        setGeneratingIds((prev) => {
          const next = new Set(prev);
          next.delete(sceneId);
          return next;
        }),
    });
  };

  // Build the normalized chapter model: each scene's prose split into paragraph
  // blocks. Empty scenes contribute nothing (so the chapter reads as not-ready).
  const readerChapters: ReaderChapter[] = useMemo(
    () =>
      chapters.map((ch) => {
        const blocks = ch.scenes.flatMap((scene) => {
          if (!hasProse(scene)) return [];
          return splitParagraphs(scene.body as string).map((para, idx) => {
            const id = `${scene.id}__${idx}`;
            // Render inline markdown (bold/italic/code) to nodes, and use the
            // marker-stripped plain text so word offsets stay aligned with the
            // rendered DOM (selection & scroll-restore read textContent).
            const { nodes, plain } = renderInlineMarkdown(para);
            return {
              id,
              text: plain,
              node: (
                <p
                  key={id}
                  data-reader-seg={id}
                  className="leading-relaxed text-slate-900 dark:text-slate-100"
                >
                  {nodes}
                </p>
              ),
            };
          });
        });
        return {
          id: ch.id,
          title: ch.title,
          ready: ch.scenes.length > 0 && ch.scenes.every(hasProse),
          pending: ch.scenes.some((s) => generatingIds.has(s.id)),
          blocks,
        };
      }),
    [chapters, generatingIds]
  );

  // The next unwritten scene that still needs a plan. Plans fill ahead even when
  // autogenerate is off — so the reader always has a plan before any prose.
  const planFrontier = useMemo(
    () => findFrontier(chapters, (s) => !hasProse(s) && !hasPlan(s)),
    [chapters]
  );
  // The next scene that has a plan but no prose — the prose autogenerate frontier.
  const proseFrontier = useMemo(
    () => findFrontier(chapters, (s) => hasPlan(s) && !hasProse(s)),
    [chapters]
  );

  // Frontier pipeline: plan first (always), then prose (only when autogenerate is
  // on). Both stay within one chapter of where you're reading, one scene at a time.
  useEffect(() => {
    if (generatingIds.size > 0) return;
    if (planFrontier && planFrontier.chapterIndex <= chapterIndex + 1) {
      requestGenerate(planFrontier.sceneId, 'plan');
      return;
    }
    if (autoGenerate && proseFrontier && proseFrontier.chapterIndex <= chapterIndex + 1) {
      requestGenerate(proseFrontier.sceneId, 'prose');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate, planFrontier, proseFrontier, chapterIndex, generatingIds]);

  // Manual: write every scene in a chapter that has a plan but no prose yet.
  const generateChapter = (ci: number) => {
    chapters[ci]?.scenes
      .filter((s) => hasPlan(s) && !hasProse(s))
      .forEach((s) => requestGenerate(s.id, 'prose'));
  };

  if (isLoading)
    return (
      <div className="grid min-h-[50vh] place-items-center">
        <Spinner />
      </div>
    );
  if (!book) return <div className="p-8 text-gray-500">{t('common.error')}</div>;

  const curChapter = readerChapters[chapterIndex];
  const chapterEmpty = !!curChapter && curChapter.blocks.length === 0;
  const curScenes = chapters[chapterIndex]?.scenes ?? [];
  const generatingInChapter = curScenes.some((s) => generatingIds.has(s.id));
  // The chapter's plan = every scene's plan points, in reading order.
  const chapterPlan = curScenes.flatMap((s) => s.planPoints.map((p) => p.content));
  const chapterHasPlan = chapterPlan.length > 0;

  let overlay: ReaderOverlay | null = null;
  if (chapterEmpty) {
    if (generatingInChapter) {
      // Plan fills before prose, so distinguish the two waits.
      overlay = {
        kind: 'spinner',
        message: chapterHasPlan
          ? t('bookReader.generatingChapter', 'Writing this chapter…')
          : t('bookReader.buildingPlan', 'Sketching the plan…'),
      };
    } else if (!chapterHasPlan) {
      overlay = {
        kind: 'hint',
        message: autoGenerate
          ? t('bookReader.waitingEarlier', 'Writing earlier chapters first…')
          : t('bookReader.planComingUp', 'Sketching the plan…'),
      };
    } else if (autoGenerate) {
      overlay = {
        kind: 'hint',
        message: t('bookReader.waitingEarlier', 'Writing earlier chapters first…'),
        planPoints: chapterPlan,
      };
    } else {
      // Plan is ready — show it and let the reader commit it to prose.
      overlay = {
        kind: 'offer',
        message: t('bookReader.chapterPlanReady', "Here's the plan for this chapter."),
        planPoints: chapterPlan,
        primaryLabel: t('bookReader.generateFromPlan', 'Generate from this plan'),
        onPrimary: () => generateChapter(chapterIndex),
        toggleLabel: t('bookReader.enableAutogenerate', 'Enable autogenerate'),
        onToggle: () => setAutoGen.mutate(true),
      };
    }
  }

  const bookUngenerated = chapters.some((ch) => ch.scenes.some((s) => !hasProse(s)));
  const chapterUngenerated = chapters[chapterIndex]?.scenes.some((s) => !hasProse(s));

  // The scene the reader is currently on — the word at the top of the page,
  // falling back to the chapter's first scene.
  const currentSceneId = () => {
    const seg = readerRef.current?.getAnchor()?.segmentId;
    return (seg ? sceneIdOfBlock(seg) : null) ?? curScenes[0]?.id ?? null;
  };

  const closePhrase = () => {
    readerRef.current?.clearSelection();
    setPhraseSel(null);
  };

  // Splice the reworded paragraph back into the scene body and save.
  const applyReword = (newParagraph: string) => {
    if (!phraseSel || !bookId) return;
    const sId = sceneIdOfBlock(phraseSel.segmentId);
    const paraIdx = Number(phraseSel.segmentId.split('__')[1]) || 0;
    const scene = chapters.flatMap((c) => c.scenes).find((s) => s.id === sId);
    if (!scene?.body) return;
    const paras = splitParagraphs(scene.body);
    if (paraIdx < 0 || paraIdx >= paras.length) return;
    paras[paraIdx] = newParagraph;
    updateBody.mutate({ sceneId: sId, content: paras.join('\n\n') }, { onSuccess: closePhrase });
  };

  const shareSelection = (text: string) => {
    if (typeof navigator.share === 'function') {
      navigator.share({ title: `“${book.title}”`, text }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(text).catch(() => {});
    }
  };

  // One-button sync: pull + reconcile + commit. If the sync is held back by a
  // conflict (or errors), send the reader to the full sync page to resolve it.
  const handleSync = () => {
    syncBook.mutate(undefined, {
      onSuccess: (result) => {
        if (result.conflicts || result.errors.length > 0) {
          navigate(`/books/${bookId}/sync`);
        }
      },
      onError: () => navigate(`/books/${bookId}/sync`),
    });
  };

  const footerActions = (
    <div className="flex shrink-0 items-center gap-1">
      <Button
        size="sm"
        variant="ghost"
        disabled={!currentSceneId()}
        onClick={() => {
          const sId = currentSceneId();
          if (sId) setSheet({ sceneId: sId, seedText: null });
        }}
      >
        ⚙ {t('bookReader.sceneButton', 'Scene')}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setCompanionOpen(true)}>
        💬 {t('bookReader.companionButton', 'Chat about this')}
      </Button>
    </div>
  );

  const headerActions = (
    <div className="flex shrink-0 items-center gap-1">
      {book.githubSyncConfigured && (
        <Button
          size="sm"
          variant="ghost"
          isLoading={syncBook.isPending}
          onClick={handleSync}
          title={t('bookReader.syncTitle', 'Sync with GitHub')}
        >
          ⟳ {t('bookReader.sync', 'Sync')}
        </Button>
      )}
      <div className="relative">
        <Button
          size="sm"
          variant={autoGenerate ? 'primary' : 'secondary'}
          onClick={() => setGenerateOpen((v) => !v)}
        >
          {t('bookReader.generate', 'Generate')} ▾
        </Button>
        <DropdownMenu
          isOpen={generateOpen}
          onClose={() => setGenerateOpen(false)}
          position="right"
          width="lg"
        >
          <DropdownMenuItem
            onClick={() => {
              setAutoGen.mutate(!autoGenerate);
              setGenerateOpen(false);
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{t('bookReader.autogenerate', 'Autogenerate')}</span>
              <span
                className={
                  autoGenerate ? 'text-xs font-semibold text-green-600' : 'text-xs text-slate-400'
                }
              >
                {autoGenerate ? t('bookReader.on', 'On') : t('bookReader.off', 'Off')}
              </span>
            </div>
            <div className="text-xs text-slate-400">
              {t(
                'bookReader.autogenerateHint',
                'Write the next scenes as you read, one chapter ahead.'
              )}
            </div>
          </DropdownMenuItem>
          <div className="my-1 border-t border-slate-100 dark:border-gray-700" />
          <DropdownMenuItem
            disabled={!chapterUngenerated}
            onClick={() => {
              generateChapter(chapterIndex);
              setGenerateOpen(false);
            }}
          >
            <div className="font-medium">
              {t('bookReader.generateChapterAction', 'Write this chapter')}
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!bookUngenerated || genBook.isPending}
            onClick={() => {
              genBook.mutate();
              setGenerateOpen(false);
            }}
          >
            <div className="font-medium">
              {t('bookReader.generateBookAction', 'Write whole book')}
            </div>
            <div className="text-xs text-slate-400">
              {t('bookReader.generateBookHint', 'May take a while.')}
            </div>
          </DropdownMenuItem>
        </DropdownMenu>
      </div>
    </div>
  );

  const breadcrumb = (
    <nav
      aria-label="Breadcrumb"
      className="mb-3 flex min-w-0 flex-wrap items-center gap-x-1.5 text-sm text-gray-500 dark:text-gray-400"
    >
      <button type="button" onClick={() => navigate('/books')} className="hover:underline">
        {t('books.title')}
      </button>
      <span aria-hidden="true">›</span>
      <button
        type="button"
        onClick={() => navigate(`/books/${bookId}/refine`)}
        className="hover:underline"
      >
        {book.title}
      </button>
      <span aria-hidden="true">›</span>
      <span className="truncate text-gray-700 dark:text-gray-300" aria-current="page">
        {t('bookReader.read', 'Read')}
      </span>
    </nav>
  );

  return (
    <>
      <Reader
        ref={readerRef}
        chapters={readerChapters}
        bookTitle={book.title}
        shareTitle={book.title}
        breadcrumb={breadcrumb}
        onExit={() => navigate(`/books/${bookId}/refine`)}
        initialPosition={book.readingPosition}
        onPersistPosition={(p) => {
          if (bookId) savePosition.mutate({ bookId, blockId: p.blockId, charOffset: p.charOffset });
        }}
        onChapterChange={setChapterIndex}
        selectionLocked={!!sheet || !!phraseSel}
        chromeBusy={generateOpen}
        headerActions={headerActions}
        footerActions={footerActions}
        overlay={overlay}
        onSelect={setPhraseSel}
      />

      {phraseSel && bookId && (
        <ScenePhraseSheet
          selection={phraseSel}
          sceneId={sceneIdOfBlock(phraseSel.segmentId)}
          saving={updateBody.isPending}
          onApply={applyReword}
          onTalk={() => {
            setSheet({ sceneId: sceneIdOfBlock(phraseSel.segmentId), seedText: phraseSel.text });
            setPhraseSel(null);
          }}
          onShare={() => shareSelection(phraseSel.text)}
          onClose={closePhrase}
        />
      )}

      {sheet && bookId && (
        <SceneToolsSheet
          key={sheet.sceneId}
          sceneId={sheet.sceneId}
          bookId={bookId}
          seedText={sheet.seedText}
          onClose={() => {
            readerRef.current?.clearSelection();
            setSheet(null);
          }}
          onOpenFullEditor={() => navigate(`/books/${bookId}/scenes/${sheet.sceneId}`)}
          onChanged={() => refetch()}
        />
      )}

      {companionOpen && bookId && (
        <BookCompanion
          bookId={bookId}
          focus="reader"
          onClose={() => setCompanionOpen(false)}
          onBookChanged={() => refetch()}
        />
      )}
    </>
  );
}
